import '@/assets/styles/views/profile/profile.scss';
import { useState, useMemo, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { VIEWS, ICONS, BUTTON_LABELS, VIEWS_MAP } from './profile-screen.constants.ts';
import { ViewType } from './profile-screen.types.ts';
import { PastMeetings, EarningsCard, ViewButton } from './_components';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider, useIsEarningsHidden } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import ProfileCard from '@/screens/profile/_components/profile-card/ProfileCard.tsx';
import { useQueryClient } from 'react-query';
import { CONTRACTS, SERVICES_DETAIL_LIST_FOR_COACHING, SERVICES_DETAIL_LIST_FOR_GOT_IT } from '@/constants';
import { Modal } from '@/components';
import { getMonthlyEarningContent } from '@/screens/profile/_components/past-meetings/get-monthly-earning.content.tsx';
import { useContractModal } from '@/hooks/useContractModal';
import { isWebView, sendContractToWebView } from '@/utility/webview.ts';

const ProfileScreen = () => {
	const queryClient = useQueryClient();
	const { PayoutControllerApi, ContractControllerApiApplicant, FeedbackControllerApi } = useApi();
	const [currentDate, setCurrentDate] = useState(dayjs());
	const [activeView, setActiveView] = useState<ViewType | null>(null);
	const [isMonthlyEarningModalOpen, setIsMonthlyEarningModalOpen] = useState<boolean>(false);
	const [contractCache, setContractCache] = useState({});
	const [isWebViewEnvironment, setIsWebViewEnvironment] = useState(false);

	const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

	const { onContractClick: originalOnContractClick, ContractModal } = useContractModal();
	const isEarningsHidden = useIsEarningsHidden();

	const referenceMonth = useMemo(() => dayjs(), []);

	useEffect(() => {
		setIsWebViewEnvironment(isWebView());
	}, []);

	const serviceList =
		queryClient.getQueryData<{
			data: { data: any };
		}>(QUERY_KEYS.getLoggedInExpertDetailInfo)?.data?.data?.serviceList || [];

	const { data: expertAverageRating } = useQueryProvider(
		[QUERY_KEYS.getExpertAverageRating, activeView, currentDate],
		() => FeedbackControllerApi.getExpertAverageRating(activeView!, parseInt(currentDate.format('YYYY')), parseInt(currentDate.format('MM'))),
		{
			refetchOnWindowFocus: false,
			enabled: !!activeView && !!currentDate,
			select: ({ data }) => data?.data
		}
	);

	const hasAppointmentService = useMemo(() => !!serviceList.find((service: any) => service.liveServiceTypeId === SERVICES_DETAIL_LIST_FOR_COACHING.COACHING_APPOINTMENTS), [serviceList]);

	const hasGotItAppointmentService = useMemo(() => !!serviceList.find((service: any) => service.liveServiceTypeId === SERVICES_DETAIL_LIST_FOR_GOT_IT.GOT_IT_APPOINTMENTS), [serviceList]);

	const visibleViews = useMemo(() => {
		const viewsToShow = [];

		const hasCoachingService = serviceList.some((service: any) => service.liveServiceTypeId === 3 || service.liveServiceTypeId === 4);

		if (hasCoachingService) {
			viewsToShow.push(VIEWS.COACHING);
		}

		const hasGotItService = serviceList.some((service: any) => service.liveServiceTypeId === 2 || service.liveServiceTypeId === 6);
		if (hasGotItService) {
			viewsToShow.push(VIEWS.GOT_IT);
		}

		const hasSolverService = serviceList.some((service: any) => service.liveServiceTypeId === 1);
		if (hasSolverService) {
			viewsToShow.push(VIEWS.SOLVER);
		}

		return viewsToShow;
	}, [serviceList]);

	const earningContentView: keyof typeof VIEWS_MAP = useMemo(() => {
		if (hasAppointmentService && activeView === VIEWS.COACHING) {
			return VIEWS_MAP.COACHING_APPOINTMENTS;
		}

		if (hasGotItAppointmentService && activeView === VIEWS.GOT_IT) {
			return VIEWS_MAP.GOT_IT_APPOINTMENTS;
		}

		return activeView;
	}, [hasAppointmentService, activeView]);

	const { data: expertPayments = [] } = useQueryProvider(
		[QUERY_KEYS.getExpertPayment, currentDate.format('YYYY'), currentDate.format('M'), activeView],
		() => PayoutControllerApi.getExpertPayment(activeView, parseInt(currentDate.format('YYYY')), parseInt(currentDate.format('M'))),
		{
			select: ({ data }) => data?.data ?? [],
			refetchOnWindowFocus: false
		}
	);

	const navigateMonth = (direction: number) => {
		const newDate = currentDate.add(direction, 'month');

		if (direction < 0 || !newDate.isAfter(referenceMonth)) {
			setCurrentDate(newDate);
		}
	};

	useMemo(() => {
		if (visibleViews.length > 0 && !visibleViews.includes(activeView)) {
			setActiveView(visibleViews[0]);
		}
	}, [visibleViews, activeView]);

	const onOpenMonthlyEarningModal = () => {
		setIsMonthlyEarningModalOpen(true);
	};

	const onCloseMonthlyEarningModal = () => {
		setIsMonthlyEarningModalOpen(false);
	};

	const monthlyEarningItems = getMonthlyEarningContent(earningContentView);

	const fetchContractDataForWebView = async (contractName: string) => {
		try {
			if (contractCache[contractName]) {
				const contract = contractCache[contractName];
				sendContractToWebView(contract, false);
				return;
			}

			const response = await ContractControllerApiApplicant.findContract(contractName);
			if (response.data && response.data.data) {
				const contractData = {
					id: response.data.data.id || 0,
					name: response.data.data.name,
					description: response.data.data.description,
					contractName: response.data.data.contractName,
					contractLink: response.data.data.contractLink
				};

				setContractCache(prev => ({
					...prev,
					[contractName]: contractData
				}));

				sendContractToWebView(contractData, false);
			}
		} catch (error) {
			console.error('Error fetching contract for WebView:', error);
		}
	};

	const onContractClick = (contractName: string) => {
		if (isWebViewEnvironment) {
			fetchContractDataForWebView(contractName);
		} else {
			originalOnContractClick(contractName);
		}
	};

	useEffect(() => {
		if (activeView && buttonRefs.current[activeView]) {
			buttonRefs.current[activeView]?.scrollIntoView({
				behavior: 'auto',
				inline: 'center',
				block: 'nearest'
			});
		}
	}, [activeView]);

	return (
		<div className="profile-page">
			<div className="left-side">
				<h4 className="profile-card-title">Profilim</h4>

				<ProfileCard />
			</div>
			<div className="right-side">
				<div className="calendar-top">
					<div className="calendar-header">
						<div className="date-navigation">
							<div className="nav-buttons">
								<button onClick={() => navigateMonth(-1)}>
									<i className={ICONS.ARROW_LEFT}></i>
								</button>
							</div>
							<h1>
								<span className="month">{currentDate.format('MMMM')}</span> <span className="year">{currentDate.format('YYYY')}</span>
							</h1>
							<div className="nav-buttons">
								<button onClick={() => navigateMonth(1)}>
									<i className={ICONS.ARROW_RIGHT}></i>
								</button>
							</div>
						</div>
						<div className="calendar-controls">
							<div className="action-buttons">
								{visibleViews?.map(view => {
									const viewKey = Object.entries(VIEWS).find(([, v]) => v === view)?.[0];
									return (
										<ViewButton
											ref={el => (buttonRefs.current[view] = el)}
											key={view}
											view={view}
											activeView={activeView}
											onClick={setActiveView}
											label={BUTTON_LABELS[viewKey as keyof typeof BUTTON_LABELS]}
										/>
									);
								})}
							</div>
						</div>
					</div>
				</div>

				{!isEarningsHidden && (
					<EarningsCard
						expertPayments={expertPayments}
						activeView={activeView}
						hasAppointmentService={hasAppointmentService}
						hasGotItAppointmentService={hasGotItAppointmentService}
						onOpenMonthlyEarningModal={onOpenMonthlyEarningModal}
						expertAverageRating={expertAverageRating}
					/>
				)}

				<PastMeetings
					currentDate={currentDate}
					activeView={activeView}
					onOpenMonthlyEarningModal={onOpenMonthlyEarningModal}
					isEarningsHidden={isEarningsHidden}
				/>

				<div className="contracts">
					<h4>Sözleşmelerim</h4>
					<div className="contract-wrapper">
						<button
							className="contract"
							onClick={() => onContractClick(CONTRACTS.CLARIFICATION_TEXT)}>
							Aydınlatma Metni
						</button>

						<button
							className="contract"
							onClick={() => onContractClick(CONTRACTS.APPLICANT_MEMBERSHIP_AGREEMENT)}>
							Bire Bir Uzman Üyelik Koşulları
						</button>

						<button
							className="contract"
							onClick={() => onContractClick(CONTRACTS.COMMERCIAL_CONFIRM_TEXT)}>
							Ticari İleti Metni
						</button>
					</div>
				</div>
			</div>
			<Modal
				isOpen={isMonthlyEarningModalOpen}
				showCloseButton
				contentClassName="monthly-earning-modal-wrapper"
				variant="centered"
				size="lg"
				onClose={onCloseMonthlyEarningModal}>
				<div className="monthly-earning-modal-container">
					<div className="monthly-earning-header">
						<h1>Aylık Kazanç Nasıl Hesaplanır?</h1>
						<p>Merak edilen tüm soruların cevaplarına anında ulaşabilirsin.</p>
					</div>
					<div className="monthly-earning-list">
						{monthlyEarningItems?.map((item, index) => (
							<div
								className="monthly-earning-item"
								key={`monthly-earning-item-${index}`}>
								<div className="monthly-earning-content-wrapper">
									<div className="icon-wrapper">
										<i className={item.icon} />
									</div>
									<h6>{item.title}</h6>
								</div>

								<div className="monthly-earning-description">{item.description}</div>
							</div>
						))}
					</div>
				</div>
			</Modal>

			{!isWebViewEnvironment && <ContractModal />}
		</div>
	);
};

export default ProfileScreen;
